// G-1 capture profile form on the Pursuit Overview tab.
//
// Opportunity name is the pursuit name (not duplicated). Current incumbent is
// the competition link with role=incumbent — this form shows that company as
// a convenience picker and writes the competition row, never a second id on
// the profile. Capture emphasis is a filter label, not capture_projects.stage.
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { createClient } from '@connectrpc/connect';
import { PursuitService } from '@mono/proto-clients';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraCardTitle, SpecraCombobox, SpecraInput, SpecraTextarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { AgencyNamePicker } from '../../components/SharedRecord/AgencyNamePicker';
import { connectTransport } from '../../lib/connect/transport';
import { PlatformUserPicker } from '../Delivery/_shared/components/PlatformUserPicker';
import { useTenantId } from '../Delivery/_shared/hooks/useTenantId';
import { useCompanyProfiles } from '../Delivery/Programs/referenceDataHooks';
import { contractVehicleHooks } from '../Delivery/Registries/hooks';
import { BID_POSITIONS, CONTRACT_TYPES, STAGE_LABELS } from './constants';
import { useDeleteCaptureCompetitionLink, useUpsertCaptureCompetitionLink, useUpsertCaptureProfile } from './hooks';
import { ReadOnlyScope } from './record/ReadOnlyScope';
const NONE = '__none__';
/** GRW-09: the vehicle picker's "Not listed" option; the free-text vehicle is kept. */ const NOT_LISTED = '__not_listed__';
/** The picker's initial choice: the linked vehicle, else "Not listed" for legacy free text. */ function vehicleChoiceOf(profile) {
    if (profile?.vehicleId) return profile.vehicleId;
    return profile?.vehicle ? NOT_LISTED : '';
}
const pursuitClient = createClient(PursuitService, connectTransport);
// PUR-01: open another Pursuit on the same Opportunity. The opening request id
// is minted once per deliberate click and kept until the open succeeds, so a
// retry after an error re-sends the same id and can never create a second one.
function useOpenAnotherPath(args) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const pendingRequestId = useRef(null);
    return useMutation({
        mutationFn: async ()=>{
            var _pendingRequestId;
            (_pendingRequestId = pendingRequestId).current ?? (_pendingRequestId.current = crypto.randomUUID());
            return pursuitClient.openPursuit({
                openingRequestId: pendingRequestId.current,
                opportunityId: args.opportunityId,
                qualificationId: args.qualificationId,
                name: `${args.pursuitName} — another path`
            });
        },
        onSuccess: (resp)=>{
            pendingRequestId.current = null;
            queryClient.invalidateQueries({
                queryKey: [
                    'capture-list'
                ]
            });
            const id = resp.summary?.captureProjectId;
            if (!id) return toast.error('OpenPursuit returned no Pursuit');
            navigate(`/capture/${id}`);
        },
        onError: (err)=>{
            toast.error(err instanceof Error ? err.message : 'Failed to open another pursuit path');
        }
    });
}
export function CaptureProfilePanel({ captureProjectId, pursuitName, profile, competitionLinks, correlationId, opportunityId = '', qualificationId = '', readOnly = false }) {
    const upsert = useUpsertCaptureProfile(correlationId);
    const upsertCompetition = useUpsertCaptureCompetitionLink(correlationId);
    const deleteCompetition = useDeleteCaptureCompetitionLink(correlationId);
    const companies = useCompanyProfiles();
    const incumbent = competitionLinks.find((l)=>l.role === 'incumbent');
    const [solicitation, setSolicitation] = useState(profile?.solicitationNumber ?? '');
    const [crm, setCrm] = useState(profile?.crmNumber ?? '');
    const [bd, setBd] = useState(profile?.businessDeveloperId ?? '');
    const [cm, setCm] = useState(profile?.captureManagerId ?? '');
    const [customer, setCustomer] = useState(profile?.customerAgencyNames ?? '');
    const [bidPosition, setBidPosition] = useState(profile?.bidPosition ?? '');
    const [vehicle, setVehicle] = useState(profile?.vehicle ?? '');
    const [vehicleChoice, setVehicleChoice] = useState(vehicleChoiceOf(profile));
    const tenantId = useTenantId();
    const vehicles = contractVehicleHooks.useList({
        companyId: tenantId
    });
    const vehicleOptions = useMemo(()=>[
            ...(vehicles.data?.items ?? []).map((v)=>({
                    value: v.id,
                    label: v.name,
                    description: [
                        v.vehicleType,
                        v.contractNumber
                    ].filter(Boolean).join(' · ') || undefined
                })),
            {
                value: NOT_LISTED,
                label: 'Not listed',
                description: 'Type the vehicle name instead'
            }
        ], [
        vehicles.data?.items
    ]);
    const [procurement, setProcurement] = useState(profile?.procurementType ?? '');
    const [contractType, setContractType] = useState(profile?.contractType ?? '');
    const [pop, setPop] = useState(profile?.periodOfPerformance ?? '');
    const [value, setValue] = useState(profile?.totalProgramValue ?? '');
    const [stageLabel, setStageLabel] = useState(profile?.stageLabel ?? '');
    const [pwin, setPwin] = useState(profile?.pwinPercent ?? '');
    const [risks, setRisks] = useState(profile?.risks ?? '');
    const [incumbentId, setIncumbentId] = useState(incumbent?.companyProfileId ?? '');
    useEffect(()=>{
        setSolicitation(profile?.solicitationNumber ?? '');
        setCrm(profile?.crmNumber ?? '');
        setBd(profile?.businessDeveloperId ?? '');
        setCm(profile?.captureManagerId ?? '');
        setCustomer(profile?.customerAgencyNames ?? '');
        setBidPosition(profile?.bidPosition ?? '');
        setVehicle(profile?.vehicle ?? '');
        setVehicleChoice(vehicleChoiceOf(profile));
        setProcurement(profile?.procurementType ?? '');
        setContractType(profile?.contractType ?? '');
        setPop(profile?.periodOfPerformance ?? '');
        setValue(profile?.totalProgramValue ?? '');
        setStageLabel(profile?.stageLabel ?? '');
        setPwin(profile?.pwinPercent ?? '');
        setRisks(profile?.risks ?? '');
    }, [
        profile
    ]);
    useEffect(()=>{
        setIncumbentId(incumbent?.companyProfileId ?? '');
    }, [
        incumbent?.id,
        incumbent?.companyProfileId
    ]);
    // One accountable internal owner (the capture manager, an account user) is
    // required; the server enforces it too.
    const ownerMissing = cm === '';
    const save = ()=>{
        if (ownerMissing) return;
        upsert.mutate({
            captureProjectId,
            id: profile?.id,
            solicitationNumber: solicitation,
            crmNumber: crm,
            businessDeveloperId: bd,
            captureManagerId: cm,
            customerAgencyNames: customer,
            bidPosition,
            vehicle,
            // GRW-09: always explicit; '' (Not listed or no vehicle) clears the registry link.
            vehicleId: vehicleChoice === NOT_LISTED ? '' : vehicleChoice,
            procurementType: procurement,
            contractType,
            periodOfPerformance: pop,
            totalProgramValue: value,
            stageLabel,
            pwinPercent: pwin,
            risks
        });
        if (!incumbentId) {
            if (incumbent?.id) {
                deleteCompetition.mutate({
                    captureProjectId,
                    id: incumbent.id
                });
            }
        } else if (incumbentId !== incumbent?.companyProfileId) {
            upsertCompetition.mutate({
                captureProjectId,
                id: incumbent?.id,
                companyProfileId: incumbentId,
                role: 'incumbent'
            });
        }
    };
    return /*#__PURE__*/ _jsx(ReadOnlyScope, {
        readOnly: readOnly,
        children: /*#__PURE__*/ _jsxs(SpecraCard, {
            "data-testid": Testids.capture.profilePanel,
            children: [
                /*#__PURE__*/ _jsxs(SpecraCardHeader, {
                    className: "flex flex-row items-center justify-between space-y-0 py-4",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraCardTitle, {
                            children: "Capture profile"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            size: "sm",
                            onClick: save,
                            disabled: ownerMissing || upsert.isPending || upsertCompetition.isPending || deleteCompetition.isPending,
                            "data-testid": Testids.capture.profileSaveButton,
                            children: upsert.isPending ? 'Saving…' : 'Save'
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs(SpecraCardContent, {
                    className: "grid gap-3 sm:grid-cols-2",
                    children: [
                        opportunityId && /*#__PURE__*/ _jsx(PursuitOpening, {
                            pursuitName: pursuitName,
                            opportunityId: opportunityId,
                            qualificationId: qualificationId
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Opportunity name",
                            children: /*#__PURE__*/ _jsx("p", {
                                className: "text-sm",
                                children: pursuitName
                            })
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Solicitation number",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: solicitation,
                                onChange: (e)=>setSolicitation(e.target.value),
                                "data-testid": Testids.capture.profileSolicitationInput
                            })
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "CRM #",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: crm,
                                onChange: (e)=>setCrm(e.target.value)
                            })
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Capture emphasis",
                            children: /*#__PURE__*/ _jsxs(Select, {
                                value: stageLabel || NONE,
                                onValueChange: (v)=>setStageLabel(v === NONE ? '' : v),
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.profileStageLabelSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {
                                            placeholder: "Filter label…"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(SelectContent, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: NONE,
                                                children: "None"
                                            }),
                                            STAGE_LABELS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: o.value,
                                                    children: o.label
                                                }, o.value))
                                        ]
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Business developer",
                            htmlFor: "capture-profile-business-developer",
                            children: /*#__PURE__*/ _jsx(PlatformUserPicker, {
                                id: "capture-profile-business-developer",
                                testid: Testids.capture.profileBusinessDeveloperSearch,
                                triggerTestid: Testids.capture.profileBusinessDeveloperInput,
                                value: bd,
                                onChange: setBd,
                                placeholder: "Search people…",
                                clearLabel: "No one"
                            })
                        }),
                        /*#__PURE__*/ _jsxs(Field, {
                            label: "Accountable owner (capture manager)",
                            htmlFor: "capture-profile-capture-manager",
                            children: [
                                /*#__PURE__*/ _jsx(PlatformUserPicker, {
                                    id: "capture-profile-capture-manager",
                                    testid: Testids.capture.profileCaptureManagerSearch,
                                    triggerTestid: Testids.capture.profileCaptureManagerInput,
                                    value: cm,
                                    onChange: setCm,
                                    placeholder: "Search people…"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: `text-xs ${ownerMissing ? 'text-destructive' : 'text-muted-foreground'}`,
                                    role: ownerMissing ? 'alert' : undefined,
                                    children: ownerMissing ? 'Required: pick the one internal account user accountable for this pursuit.' : 'Accountable owner. Must be an account user in your organization.'
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1",
                            children: [
                                /*#__PURE__*/ _jsx(Field, {
                                    label: "Customer / agency",
                                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                                        value: customer,
                                        onChange: (e)=>setCustomer(e.target.value),
                                        "data-testid": Testids.capture.profileCustomerInput
                                    })
                                }),
                                /*#__PURE__*/ _jsx(AgencyNamePicker, {
                                    onPick: setCustomer
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs(Field, {
                            label: "Current incumbent",
                            children: [
                                /*#__PURE__*/ _jsxs(Select, {
                                    value: incumbentId || NONE,
                                    onValueChange: (v)=>setIncumbentId(v === NONE ? '' : v),
                                    children: [
                                        /*#__PURE__*/ _jsx(SelectTrigger, {
                                            children: /*#__PURE__*/ _jsx(SelectValue, {
                                                placeholder: companies.isLoading ? 'Loading…' : 'Registry company…'
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs(SelectContent, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SelectItem, {
                                                    value: NONE,
                                                    children: "None"
                                                }),
                                                (companies.data ?? []).map((c)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                        value: c.id,
                                                        children: c.name
                                                    }, c.id))
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "text-xs text-muted-foreground",
                                    children: "Stored as a competition link with role=incumbent."
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Bid position",
                            children: /*#__PURE__*/ _jsxs(Select, {
                                value: bidPosition || NONE,
                                onValueChange: (v)=>setBidPosition(v === NONE ? '' : v),
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        "data-testid": Testids.capture.profileBidPositionSelect,
                                        children: /*#__PURE__*/ _jsx(SelectValue, {
                                            placeholder: "Prime / sub / JV"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(SelectContent, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: NONE,
                                                children: "None"
                                            }),
                                            BID_POSITIONS.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: o.value,
                                                    children: o.label
                                                }, o.value))
                                        ]
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs(Field, {
                            label: "Vehicle",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    "data-testid": Testids.capture.profileVehicleCombobox,
                                    children: /*#__PURE__*/ _jsx(SpecraCombobox, {
                                        clearable: true,
                                        emptyMessage: "No vehicle matches. Pick Not listed to type it.",
                                        label: "Vehicle",
                                        loading: vehicles.isLoading,
                                        onOptionChange: (option)=>{
                                            const next = option?.value ?? '';
                                            setVehicleChoice(next);
                                            // A picked vehicle also writes its name as the text (GRW-09);
                                            // Not listed keeps whatever text is there to edit.
                                            if (!next) setVehicle('');
                                            else if (next !== NOT_LISTED) setVehicle(String(option?.label ?? ''));
                                        },
                                        options: vehicleOptions,
                                        placeholder: "Pick a contract vehicle",
                                        searchPlaceholder: "Search vehicles…",
                                        selectedOption: vehicleChoice && vehicleChoice !== NOT_LISTED ? {
                                            value: vehicleChoice,
                                            label: vehicle || profile?.vehicleLabel || vehicleChoice
                                        } : undefined,
                                        value: vehicleChoice || undefined
                                    })
                                }),
                                vehicleChoice === NOT_LISTED && /*#__PURE__*/ _jsx(SpecraInput, {
                                    "aria-label": "Vehicle name",
                                    "data-testid": Testids.capture.profileVehicleText,
                                    onChange: (e)=>setVehicle(e.target.value),
                                    placeholder: "Vehicle name",
                                    value: vehicle
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Procurement type / set-aside",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: procurement,
                                onChange: (e)=>setProcurement(e.target.value)
                            })
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Contract type",
                            children: /*#__PURE__*/ _jsxs(Select, {
                                value: contractType || NONE,
                                onValueChange: (v)=>setContractType(v === NONE ? '' : v),
                                children: [
                                    /*#__PURE__*/ _jsx(SelectTrigger, {
                                        children: /*#__PURE__*/ _jsx(SelectValue, {
                                            placeholder: "T&M / FFP / …"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsxs(SelectContent, {
                                        children: [
                                            /*#__PURE__*/ _jsx(SelectItem, {
                                                value: NONE,
                                                children: "None"
                                            }),
                                            CONTRACT_TYPES.map((o)=>/*#__PURE__*/ _jsx(SelectItem, {
                                                    value: o.value,
                                                    children: o.label
                                                }, o.value))
                                        ]
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Period of performance",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: pop,
                                onChange: (e)=>setPop(e.target.value)
                            })
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "Total program value",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: value,
                                onChange: (e)=>setValue(e.target.value)
                            })
                        }),
                        /*#__PURE__*/ _jsx(Field, {
                            label: "PWin %",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: pwin,
                                onChange: (e)=>setPwin(e.target.value)
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1 sm:col-span-2",
                            children: [
                                /*#__PURE__*/ _jsx("label", {
                                    className: "text-xs font-medium",
                                    children: "Risks / issues"
                                }),
                                /*#__PURE__*/ _jsx(SpecraTextarea, {
                                    value: risks,
                                    onChange: (e)=>setRisks(e.target.value),
                                    rows: 3
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
function PursuitOpening(args) {
    const { opportunityId, qualificationId } = args;
    const openAnotherPath = useOpenAnotherPath(args);
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-wrap items-center justify-between gap-2 sm:col-span-2",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "space-y-0.5 text-sm",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            "Opportunity:",
                            ' ',
                            /*#__PURE__*/ _jsx(Link, {
                                to: "/opportunities",
                                className: "font-mono text-xs underline",
                                "data-testid": Testids.capture.pursuitOpportunityLink,
                                "data-opportunity-id": opportunityId,
                                children: opportunityId
                            })
                        ]
                    }),
                    qualificationId && /*#__PURE__*/ _jsxs("div", {
                        "data-testid": Testids.capture.pursuitQualificationId,
                        children: [
                            "Qualification: ",
                            /*#__PURE__*/ _jsx("span", {
                                className: "font-mono text-xs",
                                children: qualificationId
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                size: "sm",
                variant: "secondary",
                onClick: ()=>openAnotherPath.mutate(),
                disabled: openAnotherPath.isPending,
                "data-testid": Testids.capture.openAnotherPathButton,
                children: openAnotherPath.isPending ? 'Opening…' : 'Open another pursuit path'
            })
        ]
    });
}
function Field({ label, htmlFor, children }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-1",
        children: [
            /*#__PURE__*/ _jsx("label", {
                className: "text-xs font-medium",
                htmlFor: htmlFor,
                children: label
            }),
            children
        ]
    });
}
